/**
 * Material UI theme configuration
 * Customized to match the website branding
 */
import { createTheme } from "@mui/material/styles";

// Define color constants for consistent use throughout the theme
const colors = {
  // Primary colors (beige/cream palette)
  primary: {
    light: "#637797", // Very Light Beige/Off-white
    main: "#374151", // Muted Beige
    dark: "#262e3b", // Darker Taupe/Beige
    contrastText: "#FFFFFF", // White text for contrast on blue
  },
  // Secondary colors (blue accent colors)
  secondary: {
    light: "#ddd3c0", // Light blue
    main: "#bfae8e", // Main blue accent for interactive elements
    dark: "#87724a", // Darker blue for hover states
    contrastText: "#FFFFFF", // White text for contrast on blue
  },
  // Gray text colors
  gray: {
    light: "#F9F9F9",
    medium: "#718096",
    dark: "#2D3748",
  },
  // Dark background for admin sidebar
  darkBg: "#333D4B",
  // Brown accent for some hover states
  brown: {
    main: "#BFAE8F",
    dark: "#A69678", // Added darker brown for hover states
  },
  white: {
    main: "#FFFFFF",
    dark: "#F9F9F9",
  },
  // Status colors for chips
  status: {
    success: {
      light: "#E6F6EC",
      main: "#2E7D32",
      dark: "#1B5E20",
    },
    error: {
      light: "#FEECEB",
      main: "#D32F2F",
      dark: "#B71C1C",
    },
    warning: {
      light: "#FFF8E6",
      main: "#ED6C02",
      dark: "#E65100",
    },
    info: {
      light: "#E5F4FD",
      main: "#0288D1",
      dark: "#01579B",
    },
  },
};

const theme = createTheme({
  palette: {
    primary: {
      light: colors.primary.light,
      main: colors.primary.main,
      dark: colors.primary.dark,
      contrastText: colors.gray.dark,
    },
    secondary: {
      light: colors.secondary.light,
      main: colors.secondary.main,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrastText,
    },
    text: {
      primary: colors.gray.dark,
      secondary: colors.gray.medium,
    },
    background: {
      default: colors.gray.light,
      paper: "#FFFFFF",
      dark: colors.darkBg, // For admin sidebar
    },
    action: {
      active: colors.secondary.dark, // Changed from main to dark for better contrast
      hover: colors.secondary.light,
      selected: colors.secondary.dark, // Changed from main to dark for better contrast
      hoverOpacity: 0.15, // Increased from default 0.04
      selectedOpacity: 0.25, // Increased from default 0.08
    },
    white: {
      main: colors.white.main,
      dark: colors.white.dark,
    },
    // Additional custom colors
    accent: {
      main: colors.brown.main,
      dark: colors.brown.dark, // Added darker brown
    },
    // Enhanced status colors
    success: {
      light: colors.status.success.light,
      main: colors.status.success.main,
      dark: colors.status.success.dark,
      contrastText: colors.white.main,
    },
    error: {
      light: colors.status.error.light,
      main: colors.status.error.main,
      dark: colors.status.error.dark,
      contrastText: colors.white.main,
    },
    warning: {
      light: colors.status.warning.light,
      main: colors.status.warning.main,
      dark: colors.status.warning.dark,
      contrastText: colors.white.main,
    },
    info: {
      light: colors.status.info.light,
      main: colors.status.info.main,
      dark: colors.status.info.dark,
      contrastText: colors.white.main,
    },
  },
  typography: {
    fontFamily: "'Lexend', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontFamily: "'Playfair Display', serif",
      fontWeight: 600,
      fontSize: "2rem",
    },
    h3: {
      fontFamily: "'Lexend', sans-serif",
      fontWeight: 700,
      fontSize: "1.75rem",
    },
    h4: {
      fontFamily: "'Lexend', sans-serif",
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h5: {
      fontFamily: "'Lexend', sans-serif",
      fontWeight: 500,
      fontSize: "1.25rem",
    },
    h6: {
      fontFamily: "'Lexend', sans-serif",
      fontWeight: 500,
      fontSize: "1rem",
    },
    button: {
      textTransform: "none", // Keep original text case instead of uppercase
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global CSS overrides
        "::-webkit-scrollbar": {
          width: "8px",
        },
        "::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "::-webkit-scrollbar-thumb": {
          background: colors.primary.main,
          borderRadius: "4px",
        },
        "::-webkit-scrollbar-thumb:hover": {
          background: colors.primary.light, // Use darker brown instead of blue for scrollbar hover
        },
        // Global styles for RouterLink (React Router's Link)
        a: {
          textDecoration: "none",
          color: "inherit",
        },
        ".router-link": {
          color: colors.secondary.dark,
          textDecoration: "none",
          transition:
            "color 0.2s ease-in-out, text-decoration 0.2s ease-in-out",
          fontWeight: 500,
          "&:hover": {
            color: colors.secondary.main,
            textDecoration: "underline",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.white.dark,
          "&:hover": {
            backgroundColor: colors.primary.light,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          color: colors.white.dark,
          "&:hover": {
            backgroundColor: colors.secondary.dark,
          },
        },
        outlined: {
          borderColor: colors.primary.main,
          "&:hover": {
            backgroundColor: colors.primary.main,
            borderColor: colors.primary.main,
            color: colors.white.dark,
          },
        },
        outlinedSecondary: {
          borderColor: colors.secondary.dark,
          color: colors.secondary.dark,
          "&:hover": {
            backgroundColor: colors.secondary.dark,
            borderColor: colors.secondary.dark,
            color: colors.white.dark,
          },
        },
        text: {
          "&:hover": {
            backgroundColor: `rgba(${parseInt(
              colors.primary.main.slice(1, 3),
              16
            )}, ${parseInt(colors.primary.main.slice(3, 5), 16)}, ${parseInt(
              colors.primary.main.slice(5, 7),
              16
            )}, 0.15)`, // Increased opacity for better visibility
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: colors.gray.dark,
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          borderRadius: "8px",
          border: `1px solid ${colors.primary.light}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            "& fieldset": {
              borderColor: colors.primary.dark,
            },
            "&:hover fieldset": {
              borderColor: colors.secondary.dark, // Changed from main to dark for better contrast
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.secondary.dark, // Changed from main to dark for better contrast
            },
          },
          "& .MuiInputLabel-outlined": {
            "&.Mui-focused": {
              color: colors.secondary.dark, // Changed from main to dark for better contrast
            },
          },
          "& .MuiInputLabel-outlined.Mui-focused": {
            color: colors.secondary.dark, // Changed from main to dark for better contrast
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          borderRadius: "8px",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          borderRight: `1px solid ${colors.primary.light}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: `rgba(${parseInt(
              colors.secondary.dark.slice(1, 3), // Changed from main to dark for better contrast
              16
            )}, ${parseInt(colors.secondary.dark.slice(3, 5), 16)}, ${parseInt(
              colors.secondary.dark.slice(5, 7),
              16
            )}, 0.25)`, // Increased from 0.1 to 0.25 for better visibility
            "&:hover": {
              backgroundColor: `rgba(${parseInt(
                colors.secondary.dark.slice(1, 3), // Changed from main to dark for better contrast
                16
              )}, ${parseInt(
                colors.secondary.dark.slice(3, 5),
                16
              )}, ${parseInt(colors.secondary.dark.slice(5, 7), 16)}, 0.35)`, // Increased from 0.2 to 0.35 for better visibility
            },
          },
          "&:hover": {
            backgroundColor: `rgba(${parseInt(
              colors.secondary.dark.slice(1, 3), // Changed from main to dark for better contrast
              16
            )}, ${parseInt(colors.secondary.dark.slice(3, 5), 16)}, ${parseInt(
              colors.secondary.dark.slice(5, 7),
              16
            )}, 0.15)`, // Increased from 0.05 to 0.15 for better visibility
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.secondary.dark,
          textDecoration: "none",
          transition:
            "color 0.2s ease-in-out, text-decoration 0.2s ease-in-out",
          "&:hover": {
            color: colors.secondary.main,
            textDecoration: "underline",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.primary.dark,
          "&.Mui-checked": {
            color: colors.secondary.dark, // Changed from main to dark for better contrast
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: colors.secondary.dark, // Changed from main to dark for better contrast
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          padding: "0 8px",
          height: "32px",
          fontWeight: 500,
          transition: "all 0.2s ease-in-out",

          "&.MuiChip-filled": {
            backgroundColor: `rgba(${parseInt(
              colors.secondary.dark.slice(1, 3),
              16
            )}, ${parseInt(colors.secondary.dark.slice(3, 5), 16)}, ${parseInt(
              colors.secondary.dark.slice(5, 7),
              16
            )}, 0.15)`,
          },

          "&.MuiChip-filled.MuiChip-colorPrimary": {
            backgroundColor: colors.primary.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.primary.dark,
            },
          },

          "&.MuiChip-filled.MuiChip-colorSecondary": {
            backgroundColor: colors.secondary.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.secondary.dark,
            },
          },

          "&.MuiChip-filled.MuiChip-colorSuccess": {
            backgroundColor: colors.status.success.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.status.success.dark,
            },
          },

          "&.MuiChip-filled.MuiChip-colorError": {
            backgroundColor: colors.status.error.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.status.error.dark,
            },
          },

          "&.MuiChip-filled.MuiChip-colorWarning": {
            backgroundColor: colors.status.warning.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.status.warning.dark,
            },
          },

          "&.MuiChip-filled.MuiChip-colorInfo": {
            backgroundColor: colors.status.info.main,
            color: colors.white.main,
            "&:hover": {
              backgroundColor: colors.status.info.dark,
            },
          },

          // Outlined variants
          "&.MuiChip-outlined": {
            borderWidth: "1px",
            backgroundColor: "transparent",
          },

          "&.MuiChip-outlined.MuiChip-colorPrimary": {
            borderColor: colors.primary.main,
            color: colors.primary.main,
            "&:hover": {
              backgroundColor: `rgba(${parseInt(
                colors.primary.main.slice(1, 3),
                16
              )}, ${parseInt(colors.primary.main.slice(3, 5), 16)}, ${parseInt(
                colors.primary.main.slice(5, 7),
                16
              )}, 0.1)`,
            },
          },

          "&.MuiChip-outlined.MuiChip-colorSecondary": {
            borderColor: colors.secondary.main,
            color: colors.secondary.main,
            "&:hover": {
              backgroundColor: `rgba(${parseInt(
                colors.secondary.main.slice(1, 3),
                16
              )}, ${parseInt(
                colors.secondary.main.slice(3, 5),
                16
              )}, ${parseInt(colors.secondary.main.slice(5, 7), 16)}, 0.1)`,
            },
          },

          "&.MuiChip-outlined.MuiChip-colorSuccess": {
            borderColor: colors.status.success.main,
            color: colors.status.success.main,
            "&:hover": {
              backgroundColor: colors.status.success.light,
            },
          },

          "&.MuiChip-outlined.MuiChip-colorError": {
            borderColor: colors.status.error.main,
            color: colors.status.error.main,
            "&:hover": {
              backgroundColor: colors.status.error.light,
            },
          },

          "&.MuiChip-outlined.MuiChip-colorWarning": {
            borderColor: colors.status.warning.main,
            color: colors.status.warning.main,
            "&:hover": {
              backgroundColor: colors.status.warning.light,
            },
          },

          "&.MuiChip-outlined.MuiChip-colorInfo": {
            borderColor: colors.status.info.main,
            color: colors.status.info.main,
            "&:hover": {
              backgroundColor: colors.status.info.light,
            },
          },

          // Size variants
          "&.MuiChip-sizeSmall": {
            height: "24px",
            fontSize: "0.75rem",
          },

          // Delete icon styling
          "& .MuiChip-deleteIcon": {
            color: "inherit",
            opacity: 0.7,
            "&:hover": {
              color: "inherit",
              opacity: 1,
            },
          },

          // Label styling
          "& .MuiChip-label": {
            padding: "0 8px",
            fontWeight: 500,
          },
        },
      },
    },
  },
});

export default theme;
